import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { TemplateMatchResult } from '../../types/document-metadata';

/**
 * Icon that opens a popover with the per-anchor match breakdown computed
 * against the chosen template during the auto template-matching step.
 *
 * Both inputs are optional/nullable — when a template was pre-selected by
 * the user the backend persists nothing, so the host component should
 * simply not render this component (or pass `null`s, in which case nothing
 * visible is produced).
 */
@Component({
  selector: 'app-ocr-anchors',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, OverlayModule, A11yModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ocr-anchors.component.html'
})
export class OcrAnchorsComponent {
  /** Anchor key/value map extracted from the document. */
  documentAnchors = input<Record<string, any> | null | undefined>(null);

  /** Match result computed against the chosen template. */
  templateMatchResult = input<TemplateMatchResult | null | undefined>(null);

  /** Sorted list of anchor keys from the extracted anchors map. */
  documentAnchorKeys = computed(() => {
    const anchors = this.documentAnchors();
    return anchors && typeof anchors === 'object' ? Object.keys(anchors) : [];
  });

  /** True when there is anything to display. */
  hasContent = computed(() => !!this.templateMatchResult() || this.documentAnchorKeys().length > 0);

  readonly open = signal(false);

  readonly popoverPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 }
  ];

  readonly summary = computed(() => {
    const match = this.templateMatchResult();
    return match
      ? `Template match · Score ${this.toPercent(match.score)} · ${match.matchedAnchors}/${match.totalAnchors} anchors`
      : 'Template match';
  });

  toggle(): void {
    this.open.update(isOpen => !isOpen);
  }

  close(): void {
    this.open.set(false);
  }

  toPercent(n: number | null | undefined): string {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return `${Math.round(n * 100)}%`;
  }
}
